import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const LegalCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/legal`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching legal capabilities:', error);
      showNotification?.('Failed to load legal capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/legal`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('Legal capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving legal capabilities:', error);
      showNotification?.('Failed to save legal capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No legal capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable Legal Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle legal-related tasks including contract management, compliance checks, risk assessment, and document management.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Contract Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Contract Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.contractManagement?.enabled || false}
                  onChange={(e) => updateCapability('contractManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable contract management</span>
              </label>

              {capabilities.contractManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.contractManagement?.reviewWorkflow || false}
                      onChange={(e) => updateCapability('contractManagement.reviewWorkflow', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable review workflow</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.contractManagement?.requireApproval || false}
                      onChange={(e) => updateCapability('contractManagement.requireApproval', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Require approval for contracts</span>
                  </label>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Expiry Tracking</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.contractManagement?.expiryTracking?.enabled || false}
                        onChange={(e) => updateCapability('contractManagement.expiryTracking.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Track contract expiry</span>
                    </label>

                    {capabilities.contractManagement?.expiryTracking?.enabled && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Alert Days Before Expiry</label>
                        <input
                          type="number"
                          value={capabilities.contractManagement?.expiryTracking?.alertDaysBefore || 30}
                          onChange={(e) => updateCapability('contractManagement.expiryTracking.alertDaysBefore', Number(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-[#222222]"
                          min="1"
                        />
                      </div>
                    )}
                  </div>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Auto Renewal</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.contractManagement?.autoRenewal?.enabled || false}
                        onChange={(e) => updateCapability('contractManagement.autoRenewal.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable auto-renewal</span>
                    </label>

                    {capabilities.contractManagement?.autoRenewal?.enabled && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={capabilities.contractManagement?.autoRenewal?.requireApproval || false}
                          onChange={(e) => updateCapability('contractManagement.autoRenewal.requireApproval', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">Require approval for renewals</span>
                      </label>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Compliance Checks */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Compliance Checks</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.complianceChecks?.enabled || false}
                  onChange={(e) => updateCapability('complianceChecks.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable compliance checks</span>
              </label>

              {capabilities.complianceChecks?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.complianceChecks?.autoCheck || false}
                      onChange={(e) => updateCapability('complianceChecks.autoCheck', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-check for compliance</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.complianceChecks?.complianceReporting || false}
                      onChange={(e) => updateCapability('complianceChecks.complianceReporting', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Generate compliance reports</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Risk Assessment</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.riskAssessment?.enabled || false}
                  onChange={(e) => updateCapability('riskAssessment.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable risk assessment</span>
              </label>

              {capabilities.riskAssessment?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.riskAssessment?.autoFlag || false}
                      onChange={(e) => updateCapability('riskAssessment.autoFlag', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-flag high-risk items</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.riskAssessment?.legalHoldWorkflow || false}
                      onChange={(e) => updateCapability('riskAssessment.legalHoldWorkflow', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enable legal hold workflow</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Document Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Document Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.documentManagement?.enabled || false}
                  onChange={(e) => updateCapability('documentManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable document management</span>
              </label>
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LegalCapabilities;
